/**
 * Vorlage „Eischnee schlagen“.
 *
 * Seitenansicht einer Schüssel. Der Schneebesen kreist und dreht sich dabei,
 * das Eiweiß wird von flüssig über schaumig zu festem Schnee mit Spitzen.
 * Am Ende kommt die Probe: Die Schüssel wird gekippt, der Schnee bleibt
 * stehen, und ein Häkchen bestätigt: fertig.
 */
import { clamp, ease, lerp, segment, wave } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path } from '../paths.ts';
import { compact, faded, group, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { motionLines } from '../parts/effects.ts';
import { arcArrow, counter, doneMark, restOnGround, shadow } from '../parts/stage.ts';
import { place, whisk } from '../parts/utensils.ts';
import { bowl, interiorClip } from '../parts/vessels.ts';
import { booleanValue, defineTemplate } from '../template.ts';

/** Zeitplan. */
const TIME = {
  whisk: [0, 0.68],
  whiskOut: [0.64, 0.72],
  tilt: [0.74, 0.84],
  hold: [0.84, 0.92],
  back: [0.92, 0.97],
  /** Neubeginn: Der Schnee blendet zu flüssigem Eiweiß über, der Besen kehrt zurück. */
  reset: [0.96, 1],
} as const;

/** Schüssel. */
const BOWL = { cx: 112, rim: 86, width: 136, height: 50 } as const;

/** Größe des Schneebesens; so bleibt der Griff auch beim Herausziehen im Bild. */
const WHISK_SCALE = 1.1;

/** Kippwinkel bei der Probe. Bei 75 Grad würde flüssiges Eiweiß sicher herauslaufen. */
const TILT = -62;

/**
 * Eiweiß als Körper: oben ein Profil (flüssig flach, steif mit Wölbung und
 * Spitzen), unten die vordere Hälfte der Oberflächenellipse.
 */
function whiteBody(cx: number, y: number, rx: number, ry: number, firmness: number, t: number): string {
  const steps = 28;
  const builder = path().move(cx - rx, y);
  for (let index = 1; index < steps; index += 1) {
    const f = index / steps;
    const x = cx - rx + f * rx * 2;
    // Flüssig: hintere Hälfte der Ellipse, leicht bewegt.
    const back = -Math.sin(f * Math.PI) * ry;
    const ripple = wave(f, 3, t * 6) * 0.8 * (1 - firmness);
    // Steif: Wölbung mit Spitzen, die über den Rand ragen.
    const dome = Math.sin(f * Math.PI) ** 0.8 * 9 * firmness;
    const peaks = Math.max(0, wave(f, 3.5, 0.1)) ** 4 * 8 * firmness;
    builder.line(x, y + back * (1 - firmness * 0.4) - dome - peaks + ripple);
  }
  builder.line(cx + rx, y);
  builder.arc(rx, ry, false, true, cx - rx, y);
  return builder.close().toString();
}

/** Vorlage „Eischnee schlagen“. */
export const whiskStiffTemplate = defineTemplate({
  id: 'mix.whisk-stiff',
  group: 'mix',
  title: 'Eischnee schlagen',
  description:
    'Eiweiß wird mit dem Schneebesen aufgeschlagen, bis feste Spitzen stehen. Bei der Probe bleibt der Schnee in der gekippten Schüssel.',
  durationSeconds: 7,
  keywords: ['eischnee', 'eiweiß steif', 'eiweiss steif', 'steif schlagen', 'sahne steif', 'aufschlagen'],
  stillTime: 0.6,
  parameters: [{ kind: 'boolean', name: 'test', label: 'Probe am Ende zeigen', defaultValue: true }],
  scene(values, t) {
    const test = booleanValue(values, 'test', true);
    const whiskPhase = segment(t, TIME.whisk[0], TIME.whisk[1]);
    const firmness = ease.inOutSine(clamp(whiskPhase * 1.15));
    const tilt = test
      ? TILT * (ease.inOutCubic(segment(t, ...TIME.tilt)) - ease.inOutCubic(segment(t, ...TIME.back)))
      : 0;
    const vessel = bowl(BOWL.cx, BOWL.rim, BOWL.width, BOWL.height);

    // Eiweiß: Die Oberfläche steigt mit dem Volumen, bleibt aber unter dem
    // Rand, damit der Schnee beim Kippen als Block in der Schüssel bleibt.
    const surface = (amount: number) => {
      const level = lerp(0.5, 0.3, amount);
      const rx = vessel.widthAt(level);
      return { y: BOWL.rim + vessel.depth * level, rx, ry: vessel.ry * (rx / vessel.rx) };
    };
    const { y: surfaceY, rx } = surface(firmness);
    const whiteShape = (amount: number): SceneNode => {
      const { y, rx: width, ry } = surface(amount);
      // Flüssiges Eiweiß ist glasig (Zwischenton), fester Schnee hell.
      const style =
        amount > 0.3 ? styles.snow() : ({ fill: 'tint', stroke: 'ink', strokeWidth: STROKE.detail } as const);
      return shape(whiteBody(BOWL.cx, y, width, ry, amount, t), style);
    };
    // Zum Neubeginn liegt flüssiges Eiweiß unter dem Schnee, der ausgeblendet wird.
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const white = group(
      reset > 0 ? [whiteShape(0), faded(1 - reset, [whiteShape(firmness)])] : [whiteShape(firmness)],
      {
        clip: interiorClip(vessel, 24),
      },
    );

    // Schneebesen: kreist in der Schüssel und dreht sich dabei. Zum Schluss
    // wird er schräg nach rechts herausgezogen und ausgeblendet.
    const whiskOut = ease.inOutCubic(segment(t, ...TIME.whiskOut));
    const turns = 6;
    const angle = whiskPhase * turns * Math.PI * 2;
    const wx = BOWL.cx + Math.cos(angle) * rx * 0.45 + whiskOut * 50;
    const wy = surfaceY + 4 + Math.sin(angle) * 4 - whiskOut * 16;
    const lean = -14 + Math.cos(angle) * 8 + whiskOut * 40;
    const start = surface(0);
    const whiskNode =
      reset > 0
        ? faded(reset, [place(BOWL.cx + start.rx * 0.45, start.y + 4, -6, whisk(0), WHISK_SCALE)])
        : faded(1 - segment(whiskOut, 0.4, 1), [
            place(wx, wy, lean, whisk(whiskPhase * turns * 2), WHISK_SCALE),
          ]);

    // Reihenfolge: Innenwand, Eiweiß, Besen, Vorderwand. Der Besen steckt im
    // Eiweiß; was unter dem vorderen Rand liegt, verdeckt die Wand.
    const bowlNodes: SceneNode[] = compact([...vessel.back, white, whiskNode, ...vessel.front]);
    const motion =
      reset > 0
        ? motionLines(BOWL.cx + BOWL.width / 2 + 12, start.y - 4, 200, 12, 3, 6, reset * 0.9)
        : whiskOut < 0.5
          ? motionLines(BOWL.cx + BOWL.width / 2 + 12, surfaceY - 4, 200, 12, 3, 6, (1 - whiskOut * 2) * 0.9)
          : null;

    // Bei der Probe kippt die Schüssel und liegt dabei mit dem tiefsten Punkt
    // auf der Arbeitsfläche auf, statt zu schweben.
    const origin = { x: BOWL.cx, y: BOWL.rim + BOWL.height / 2 };
    const rest = restOnGround(vessel.contour, tilt, origin);
    const tilted = Math.abs(tilt / TILT);
    return compact([
      ...counter(),
      shadow(lerp(BOWL.cx, rest.contact.x + 14, tilted), lerp(BOWL.width * 0.3, 22, tilted)),
      group(bowlNodes, { transform: { y: rest.dy, rotate: tilt, ox: origin.x, oy: origin.y } }),
      motion,
      test && t > TIME.hold[0] && t < 0.99
        ? group([doneMark(196, 40, 1.2)], {
            opacity:
              ease.outCubic(segment(t, TIME.hold[0], TIME.hold[0] + 0.03)) * (1 - segment(t, 0.95, 0.99)),
          })
        : null,
    ]);
  },
  hints() {
    return [arcArrow(BOWL.cx, 58, 44, 11, 200, 340)];
  },
});
